/**
 * softLightConfig — the declarative config + pure resolver for a SoftLight.
 *
 * A SoftLight is the core "djmsqrvve light": a heavily-blurred radial glow
 * (the `glow_soft` texture) that slow-drifts on an eased path while its
 * opacity gently breathes. This module owns ONLY the data + the math that
 * turns a partial config into a fully-resolved spec (defaults + seeded
 * per-light variation). The SoftLight class (softLight.ts) does the Pixi glue.
 *
 * Splitting it this way keeps the variation math (drift bearing, period band,
 * alpha clamp) unit-testable without a renderer — the same discipline as
 * atmospherePlacement.ts.
 *
 * @see ./SPEC.md — language-neutral lift spec (config + algorithm + renderer seam).
 */
import { seededRand } from '../atmosphere/atmospherePlacement';
import type { VfxBlendMode } from '../../rendering/IRenderer';

/** Author-facing config for a SoftLight — every field optional (defaulted). */
export interface SoftLightConfig {
  /** Glow sprite diameter in px (these are big + soft — 80–280 reads well). */
  size?: number;
  /** Tint applied to the white glow texture. */
  color?: number;
  /** Opacity floor of the breathing pulse. */
  alphaMin?: number;
  /** Opacity ceiling of the breathing pulse. */
  alphaMax?: number;
  /** How far (px) the light drifts from its anchor before easing back. */
  driftRadius?: number;
  /** Drift + pulse cycle length band (ms) — slow on purpose. A per-light
   *  value is chosen from this band by the seed so lights fall out of phase. */
  periodMinMs?: number;
  periodMaxMs?: number;
  /** 'add' (default) or 'screen' compositing — both read as light. */
  blendMode?: VfxBlendMode;
}

/** Fully-resolved, render-ready SoftLight parameters. */
export interface SoftLightSpec {
  size: number;
  color: number;
  alphaMin: number;
  alphaMax: number;
  driftRadius: number;
  /** The chosen cycle length for THIS light (ms). */
  periodMs: number;
  /** The chosen drift bearing for THIS light (radians). */
  driftAngleRad: number;
  blendMode: VfxBlendMode;
}

/**
 * Defaults tuned for the calm, premium ambience — large, soft, low-opacity
 * lights that drift slowly. Deliberately sparse/subtle: the look comes from
 * overlap + slow motion, not brightness.
 */
export const DEFAULT_SOFT_LIGHT = {
  size: 160,
  color: 0x9fd8ff, // cool pale blue — neutral; callers tint per palette
  alphaMin: 0.06,
  alphaMax: 0.28,
  driftRadius: 36,
  periodMinMs: 7000,
  periodMaxMs: 15000,
  blendMode: 'add' as VfxBlendMode,
} as const;

/**
 * Resolve a partial config into a render-ready spec. `seed` drives the
 * per-light variation (drift bearing + cycle length) deterministically, so
 * a field of lights placed with stable seeds is stable across reloads (no
 * flicker) yet each light moves independently.
 */
export function resolveSoftLight(cfg: SoftLightConfig, seed = 0): SoftLightSpec {
  const d = DEFAULT_SOFT_LIGHT;
  const periodMin = cfg.periodMinMs ?? d.periodMinMs;
  const periodMax = cfg.periodMaxMs ?? d.periodMaxMs;

  let alphaMin = cfg.alphaMin ?? d.alphaMin;
  let alphaMax = cfg.alphaMax ?? d.alphaMax;
  // Never let a mis-ordered config invert the pulse.
  if (alphaMin > alphaMax) [alphaMin, alphaMax] = [alphaMax, alphaMin];

  const angleRoll = seededRand(seed * 1.37 + 11);
  const periodRoll = seededRand(seed * 2.11 + 29);

  return {
    size: cfg.size ?? d.size,
    color: cfg.color ?? d.color,
    alphaMin,
    alphaMax,
    driftRadius: cfg.driftRadius ?? d.driftRadius,
    periodMs: periodMin + (periodMax - periodMin) * periodRoll,
    driftAngleRad: angleRoll * Math.PI * 2,
    blendMode: cfg.blendMode ?? d.blendMode,
  };
}
